import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { markAsWatched, removeFromWatched, isWatched } from '../../lib/supabase';
import { hapticSuccess } from '../../lib/haptics';

interface Props {
  tmdbId: number;
  title: string;
  year: number;
  posterUrl: string | null;
  overview: string;
  className?: string;
}

export function WatchedButton({
  tmdbId,
  title,
  year,
  posterUrl,
  overview,
  className
}: Props) {
  const [isMarkedWatched, setIsMarkedWatched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkWatchedStatus();
  }, [tmdbId]);

  const checkWatchedStatus = async () => {
    try {
      const status = await isWatched(tmdbId);
      setIsMarkedWatched(status);
    } catch (err) {
      console.error('Error checking watched status:', err);
    }
  };

  const handleClick = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);

    try {
      if (isMarkedWatched) {
        await removeFromWatched(tmdbId);
        setIsMarkedWatched(false);
      } else {
        const result = await markAsWatched({
          tmdb_id: tmdbId,
          title,
          year,
          poster_url: posterUrl,
          overview
        });
        if (result !== null) {
          setIsMarkedWatched(true);
          void hapticSuccess();
        } else {
          // Movie already watched (409 conflict)
          setIsMarkedWatched(true);
        }
      }
    } catch (err) {
      console.error('Error updating watched status:', err);
      if (err instanceof Error && err.message.includes('Authentication required')) {
        setError('Please sign in to mark movies as watched');
      } else if (err instanceof Error && err.message.includes('Network')) {
        setError('Connection issue. Please check your internet and try again');
      } else {
        setError('Unable to update watched status. Please try again');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={cn(
          'rounded-full border p-2.5 transition-all duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          isMarkedWatched
            ? 'border-emerald-400/40 bg-emerald-400/15 text-emerald-400 hover:bg-emerald-400/20'
            : 'border-white/10 bg-white/[0.03] text-muted-foreground hover:border-emerald-400/40 hover:text-emerald-400',
          isLoading && 'cursor-not-allowed opacity-50',
          className
        )}
        title={isMarkedWatched ? 'Mark as unwatched' : 'Mark as watched'}
      >
        <Check className={cn(
          'h-5 w-5 transition-transform duration-200',
          isMarkedWatched && 'scale-110',
          isLoading && 'animate-pulse'
        )} />
      </button>
      {error && (
        <div className="absolute bottom-full left-1/2 z-30 mb-2 w-48 -translate-x-1/2 rounded-lg border border-destructive/30 bg-popover p-2 text-xs text-red-300 shadow-deep">
          {error}
        </div>
      )}
    </div>
  );
}